// CalibrationParameterFields.js
// Shared calibration parameter fields component (used by the Sampling Event form and the
// standalone Calibration Log Book). Uses tempState pattern - all data comes from tempLogData prop,
// updates via updateTempLog. Fully context-agnostic (only couples to selectedMeter.calibration_parameters).

import React, { useState, useEffect } from "react";
import Select from "react-select";
// Styles
import styles from "../CalibrationLog.module.css";

/**
 * Calibration Parameter Fields Component
 * Displays calibration parameters for the selected meter and allows user input.
 * Pass/fail is automatically calculated based on verification limits.
 *
 * @param {string} logId - The ID for this log entry (either existing UUID or new temp UUID)
 * @param {object} tempLogData - The temporary log data from tempState.logs[logId]
 * @param {function} updateTempLog - Function to update tempState: (logId, updates) => void
 * @param {function} onRemoveLog - Function to remove this log from tempState
 * @param {object} selectedMeter - The selected meter with calibration_parameters
 * @param {boolean} canRemove - Whether this log can be removed (false if it's the only one)
 */
export const CalibrationParameterFields = ({
  logId,
  tempLogData,
  updateTempLog,
  onRemoveLog,
  selectedMeter,
  canRemove = true,
}) => {
  // Local state for verification input (to handle typing before blur)
  const [localVerification, setLocalVerification] = useState(
    tempLogData?.verification || ""
  );

  // Sync local verification with tempLogData when it changes externally
  useEffect(() => {
    setLocalVerification(tempLogData?.verification || "");
  }, [tempLogData?.verification]);

  // Early return if no tempLogData (shouldn't happen, but safety check)
  if (!tempLogData) {
    return null;
  }

  /**
   * Handles field parameter selection from dropdown.
   * Auto-fills standard, units, and stores verification limits.
   */
  const handleFieldParameterChange = (selected) => {
    if (!selected) {
      updateTempLog(logId, {
        fieldParameter: "",
        standardUsed: "",
        units: "",
        verification: "",
        passFail: "",
        standardTop: null,
        standardBottom: null,
      });
      setLocalVerification("");
      return;
    }

    updateTempLog(logId, {
      fieldParameter: selected.value,
      standardUsed: selected.standard_used || "",
      units: selected.verification_units || "",
      verification: "",
      passFail: "",
      standardTop: selected.standard_top,
      standardBottom: selected.standard_bottom,
    });
    setLocalVerification("");
  };

  /**
   * Handles manual units change (for blank params or custom entry)
   */
  const handleUnitsChange = (e) => {
    updateTempLog(logId, { units: e.target.value });
  };

  /**
   * Handles manual calibration standard change
   */
  const handleCalibrationStandardChange = (e) => {
    updateTempLog(logId, { standardUsed: e.target.value });
  };

  /**
   * Handles verification value change on blur.
   * Calculates pass/fail based on stored limits.
   */
  const handleVerificationBlur = () => {
    const value = localVerification;
    const numValue = parseFloat(value);

    if (!value || isNaN(numValue)) {
      updateTempLog(logId, { verification: "", passFail: "" });
      return;
    }

    // Calculate pass/fail based on limits
    const { standardTop, standardBottom } = tempLogData;
    let passFail = "";

    if (standardTop !== null && standardBottom !== null) {
      if (numValue < standardBottom || numValue > standardTop) {
        passFail = "Fail";
      } else {
        passFail = "Pass";
      }
    }

    updateTempLog(logId, {
      verification: value,
      passFail: passFail,
    });
  };

  /**
   * Handles manual pass/fail override
   */
  const handlePassFailChange = (e) => {
    updateTempLog(logId, { passFail: e.target.value });
  };

  return (
    <div
      className={styles.modalParameterRow}
      data-internal-id={logId}
      key={logId}
    >
      <div className={styles.modalField}>
        <label className={styles.modalLabel}>Field Param</label>
        <Select
          options={
            selectedMeter?.calibration_parameters?.map((parameter) => ({
              label: parameter.field_parameter,
              value: parameter.field_parameter,
              standard_used: parameter.calibration_standard,
              standard_top: parameter.calibration_verification_top,
              standard_bottom: parameter.calibration_verification_bottom,
              verification_units: parameter.calibration_verification_units,
            })) || []
          }
          value={
            tempLogData.fieldParameter
              ? {
                  label: tempLogData.fieldParameter,
                  value: tempLogData.fieldParameter,
                }
              : null
          }
          onChange={handleFieldParameterChange}
          placeholder="Select field parameter"
          isClearable
        />
      </div>
      <div className={styles.modalField}>
        <label className={styles.modalLabel}>Cal Standard</label>
        <input
          type="text"
          className={styles.modalInput}
          value={tempLogData.standardUsed || ""}
          onChange={handleCalibrationStandardChange}
          placeholder="Enter calibration standard"
        />
      </div>
      <div className={styles.modalField}>
        <label className={styles.modalLabel}>Verification</label>
        <div className={styles.modalVerificationInput}>
          <input
            type="number"
            className={styles.modalInput}
            value={localVerification}
            onChange={(e) => setLocalVerification(e.target.value)}
            onBlur={handleVerificationBlur}
            placeholder="Enter verification"
            max={9999999999}
          />
        </div>
      </div>
      <div className={styles.modalField}>
        <label className={styles.modalLabel}>Unit</label>
        <div className={styles.modalUnitInput}>
          <input
            type="text"
            className={styles.modalInput}
            value={tempLogData.units || ""}
            onChange={handleUnitsChange}
          />
        </div>
      </div>
      <div className={styles.modalField}>
        <label className={styles.modalLabel}>Pass/Fail</label>
        <select
          className={styles.modalSelect}
          value={tempLogData.passFail || ""}
          onChange={handlePassFailChange}
        >
          <option value="">Select</option>
          <option value="Pass">Pass</option>
          <option value="Fail">Fail</option>
        </select>
      </div>
      <div className={styles.modalField}>
        {canRemove && (
          <button
            className={styles.removeParamButton}
            onClick={() => onRemoveLog(logId)}
            type="button"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};
